import { Show, createMemo } from "solid-js";
import { t } from "../i18n";
import {
  type ActiveWorkoutSession,
  formatElapsed,
  isWorkoutPaused,
} from "../lib/activeWorkout";
import {
  addRestSeconds,
  dismissRestFinished,
  formatRestClock,
  pauseRestTimer,
  resetRestTimer,
  restProgress,
  restTimerState,
  resumeRestTimer,
  startRestTimer,
} from "../lib/restTimer";

interface Props {
  session: ActiveWorkoutSession;
  now: number;
  doneCount: number;
  totalSets: number;
}

/** Sticky top bar: workout duration, swapped for rest timer when active. */
export default function ActiveWorkoutTimerBar(props: Props) {
  const getState = restTimerState();

  const status = () => getState().status;
  const restActive = () =>
    status() === "running" ||
    status() === "paused" ||
    status() === "finished";

  const remaining = () => {
    const n = getState().remainingSec;
    return Number.isFinite(n) ? n : 0;
  };

  const lastPreset = () => {
    const n = getState().lastPreset;
    return Number.isFinite(n) && n > 0 ? n : 90;
  };

  const displayRest = () =>
    status() === "idle" ? lastPreset() : remaining();

  const tone = createMemo(() => {
    if (status() === "finished") return "done";
    if (status() === "paused") return "warn";
    if (status() === "running") {
      const p = restProgress();
      if (p > 0.45) return "ok";
      if (p > 0.2) return "warn";
      return "urgent";
    }
    return "idle";
  });

  /** SVG ring: circumference of r=18 → ~113.1 */
  const ringC = 2 * Math.PI * 18;
  const ringOffset = () => {
    if (status() === "finished") return 0;
    const p = restProgress();
    return ringC * (1 - Math.min(1, Math.max(0, p)));
  };

  return (
    <div
      class={`aw-timer-bar ${restActive() ? `aw-timer-bar--rest tone-${tone()}` : "aw-timer-bar--duration"}`}
      role="timer"
      aria-live="polite"
      aria-atomic="true"
    >
      <div class="aw-timer-bar-inner">
        {/* Fixed-height stage so list does not jump when mode swaps */}
        <div class="aw-timer-stage">
          <Show
            when={restActive()}
            fallback={
              <div class="aw-timer-mode aw-timer-mode--duration">
                <div class="aw-timer-meta">
                  <span class="aw-timer-label">
                    {isWorkoutPaused(props.session)
                      ? t("WORKOUT_PAUSED")
                      : t("ACTIVE_WORKOUT")}
                  </span>
                  <Show when={props.totalSets > 0}>
                    <span class="aw-timer-sets">
                      {props.doneCount}/{props.totalSets}{" "}
                      {t("SETS").toLowerCase()}
                    </span>
                  </Show>
                </div>
                <div
                  class={`aw-timer-clock aw-timer-clock--duration tabular-nums ${
                    isWorkoutPaused(props.session) ? "is-paused" : ""
                  }`}
                >
                  {formatElapsed(props.session, props.now)}
                </div>
              </div>
            }
          >
            <div class={`aw-timer-mode aw-timer-mode--rest tone-${tone()}`}>
              <div class="aw-rest-main">
                <div
                  class={`aw-rest-ring-wrap ${
                    status() === "finished" ? "is-done" : ""
                  }`}
                  aria-hidden="true"
                >
                  <svg class="aw-rest-ring" viewBox="0 0 44 44">
                    <circle
                      class="aw-rest-ring-track"
                      cx="22"
                      cy="22"
                      r="18"
                      fill="none"
                      stroke-width="3"
                    />
                    <circle
                      class="aw-rest-ring-progress"
                      cx="22"
                      cy="22"
                      r="18"
                      fill="none"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-dasharray={String(ringC)}
                      stroke-dashoffset={String(ringOffset())}
                      transform="rotate(-90 22 22)"
                    />
                  </svg>
                </div>

                <div class="aw-rest-text">
                  <div class="aw-timer-meta">
                    <span class="aw-timer-label aw-timer-label--rest">
                      {status() === "finished"
                        ? t("REST_DONE")
                        : status() === "paused"
                          ? t("REST_PAUSED")
                          : t("REST_TIMER")}
                    </span>
                  </div>
                  <div
                    class={`aw-timer-clock aw-timer-clock--rest tabular-nums ${
                      status() === "finished" ? "is-done" : ""
                    }`}
                  >
                    {formatRestClock(displayRest())}
                  </div>
                </div>

                <div class="aw-rest-controls">
                  <Show when={status() === "finished"}>
                    <button
                      type="button"
                      class="aw-rest-btn aw-rest-btn--primary"
                      onClick={() => dismissRestFinished()}
                    >
                      {t("DONE")}
                    </button>
                    <button
                      type="button"
                      class="aw-rest-btn"
                      onClick={() => startRestTimer(lastPreset())}
                    >
                      {t("REST_AGAIN")}
                    </button>
                  </Show>

                  <Show when={status() === "running"}>
                    <button
                      type="button"
                      class="aw-rest-btn"
                      aria-label={t("REST_PAUSE")}
                      onClick={() => pauseRestTimer()}
                    >
                      ⏸
                    </button>
                    <button
                      type="button"
                      class="aw-rest-btn"
                      onClick={() => addRestSeconds(15)}
                    >
                      +15
                    </button>
                    <button
                      type="button"
                      class="aw-rest-btn"
                      aria-label={t("REST_RESET")}
                      onClick={() => resetRestTimer()}
                    >
                      ↻
                    </button>
                  </Show>

                  <Show when={status() === "paused"}>
                    <button
                      type="button"
                      class="aw-rest-btn aw-rest-btn--primary"
                      aria-label={t("REST_RESUME")}
                      onClick={() => resumeRestTimer()}
                    >
                      ▶
                    </button>
                    <button
                      type="button"
                      class="aw-rest-btn"
                      onClick={() => addRestSeconds(15)}
                    >
                      +15
                    </button>
                    <button
                      type="button"
                      class="aw-rest-btn"
                      aria-label={t("REST_RESET")}
                      onClick={() => resetRestTimer()}
                    >
                      ↻
                    </button>
                  </Show>
                </div>
              </div>

              <Show when={status() === "running" || status() === "paused"}>
                <div class="aw-rest-bar-track" aria-hidden="true">
                  <div
                    class="aw-rest-bar-fill"
                    style={{ width: `${restProgress() * 100}%` }}
                  />
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}
